import React from 'react';
import { Link } from 'react-router-dom';
import { adminService } from '../api/services';
import useApi from '../hooks/useApi';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Users, ShieldCheck, Video, Calendar, TrendingUp, AlertCircle, ArrowRight, Clock, History } from 'lucide-react';
import { ActivityHistory } from '../components/shared/ActivityHistory';

const StatCard = ({ icon: Icon, label, value, sub, color, to }) => {
  const inner = (
    <div className="bg-white border border-cream-200 rounded-2xl p-5 hover:shadow-sm transition-all hover:-translate-y-0.5 group">
      <div className="flex items-start justify-between mb-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}><Icon size={18} /></div>
        {to && <ArrowRight size={14} className="text-forest-300 group-hover:text-forest-600 transition-colors" />}
      </div>
      <div className="font-serif text-3xl font-bold text-forest-900">{value ?? '—'}</div>
      <div className="text-sm font-medium text-forest-700 mt-1">{label}</div>
      {sub && <div className="text-xs text-forest-400 mt-0.5">{sub}</div>}
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
};

const AdminDashboardPage = () => {
  const { data, loading, error } = useApi(() => adminService.getDashboard());
  const overview = data?.overview ?? {};
  const recentRegistrations = data?.recentRegistrations ?? [];
  // Platform-wide session history — use admin sessions endpoint
  const { data: allSessionsData } = useApi(() => adminService.getAllSessions({ limit: 200 }));
  const rawSessions = allSessionsData?.sessions ?? [];
  // Map sessions to the shape ActivityHistory expects (booking-like)
  const allBookings = rawSessions.map(s => ({
    id:           s.id,
    status:       s.status,
    sessionType:  s.sessionType,
    scheduledAt:  s.scheduledAt,
    duration:     s.duration,
    expertProfile: s.expert ? { id: s.expert.id, user: { firstName: s.expert.name?.split(' ')[0] ?? '', lastName: s.expert.name?.split(' ').slice(1).join(' ') ?? '' } } : null,
    clientProfile: s.clients?.[0] ? { id: s.clients[0].id, user: { firstName: s.clients[0].name?.split(' ')[0] ?? '', lastName: s.clients[0].name?.split(' ').slice(1).join(' ') ?? '' } } : null,
  }));

  // ✅ FIX: backend returns levelDistribution as [{level: {name}, clientCount}]
  // Map it to a consistent shape for rendering
  const levelDistribution = (data?.levelDistribution ?? []).map(item => ({
    name: item.level?.name ?? item.levelName ?? 'Unknown',
    count: item.clientCount ?? item.count ?? 0,
  }));

  return (
    <DashboardLayout title="Admin Dashboard">
      <div className="space-y-8 max-w-5xl">

        {error && (
          <div className="flex items-center gap-3 text-rust-500 bg-rust-50 border border-rust-200 rounded-xl px-5 py-4">
            <AlertCircle size={16} className="flex-shrink-0" />
            <span className="text-sm">Could not load dashboard — make sure your API server is running.</span>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Users}       label="Total Clients"    value={loading ? '...' : overview.totalClients}    color="text-forest-600 bg-forest-50"  to="/admin/clients" />
          <StatCard icon={ShieldCheck} label="Total Experts"    value={loading ? '...' : overview.totalExperts}    color="text-blue-600 bg-blue-50"      to="/admin/experts" />
          <StatCard icon={Clock}       label="Pending Experts"  value={loading ? '...' : overview.pendingExperts}  color="text-amber-600 bg-amber-50"    to="/admin/experts" sub="Awaiting review" />
          <StatCard icon={Video}       label="Live Sessions"    value={loading ? '...' : overview.liveSessions}    color="text-green-600 bg-green-50" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Calendar}   label="Total Bookings"   value={loading ? '...' : overview.totalBookings}   color="text-purple-600 bg-purple-50" />
          <StatCard icon={Clock}      label="Pending Bookings" value={loading ? '...' : overview.pendingBookings} color="text-rust-500 bg-rust-50/50" />
          <StatCard icon={Video}      label="Total Sessions"   value={loading ? '...' : overview.totalSessions}   color="text-forest-600 bg-forest-50" />
          <StatCard icon={TrendingUp} label="Total Levels"     value={loading ? '...' : overview.totalLevels}     color="text-amber-600 bg-amber-50"   to="/admin/levels" />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white border border-cream-200 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-serif text-lg font-semibold text-forest-900">Clients per Level</h3>
              <Link to="/admin/levels" className="text-xs text-forest-500 hover:text-forest-800 font-medium">Manage →</Link>
            </div>
            {loading ? (
              <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-10 bg-cream-100 rounded-xl animate-pulse" />)}</div>
            ) : levelDistribution.length === 0 ? (
              <p className="text-sm text-forest-400 text-center py-6">No data yet</p>
            ) : (
              <div className="space-y-3">
                {levelDistribution.map((lvl, i) => {
                  const max = Math.max(...levelDistribution.map(l => l.count), 1);
                  const pct = Math.round((lvl.count / max) * 100);
                  return (
                    <div key={i}>
                      <div className="flex items-center justify-between text-xs mb-1.5">
                        <span className="font-medium text-forest-700 truncate max-w-[60%]">{lvl.name}</span>
                        <span className="text-forest-500 font-semibold">{lvl.count} clients</span>
                      </div>
                      <div className="h-2 bg-cream-100 rounded-full overflow-hidden">
                        <div className="h-full bg-forest-600 rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="bg-white border border-cream-200 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-serif text-lg font-semibold text-forest-900">Recent Registrations</h3>
              <Link to="/admin/clients" className="text-xs text-forest-500 hover:text-forest-800 font-medium">View all →</Link>
            </div>
            {loading ? (
              <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-12 bg-cream-100 rounded-xl animate-pulse" />)}</div>
            ) : recentRegistrations.length === 0 ? (
              <p className="text-sm text-forest-400 text-center py-6">No recent registrations</p>
            ) : (
              <div className="space-y-3">
                {recentRegistrations.slice(0, 6).map((u, i) => {
                  const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
                  const date = new Date(u.createdAt).toLocaleDateString('en-GH', { day: 'numeric', month: 'short' });
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-forest-700 rounded-full flex items-center justify-center text-cream-50 font-serif text-xs font-bold flex-shrink-0">
                        {name[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-forest-900 truncate">{name}</div>
                        <div className="text-xs text-forest-400">{u.role?.toLowerCase()}</div>
                      </div>
                      <span className="text-xs text-forest-400 flex-shrink-0">{date}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div>
          <h3 className="font-serif text-lg text-forest-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[
              { label: 'Review Pending Experts', to: '/admin/experts', icon: ShieldCheck, badge: overview.pendingExperts },
              { label: 'Manage Levels',           to: '/admin/levels',  icon: TrendingUp },
              { label: 'Browse Clients',          to: '/admin/clients', icon: Users },
            ].map((action) => (
              <Link key={action.label} to={action.to}
                className="bg-white border border-cream-200 rounded-2xl p-4 flex items-center gap-3 hover:shadow-sm hover:border-forest-200 transition-all group">
                <div className="w-9 h-9 bg-forest-50 rounded-xl flex items-center justify-center group-hover:bg-forest-100 transition-colors flex-shrink-0">
                  <action.icon size={16} className="text-forest-600" />
                </div>
                <span className="text-sm font-medium text-forest-700 flex-1">{action.label}</span>
                {action.badge > 0 && (
                  <span className="w-5 h-5 bg-rust-500 rounded-full text-white text-[10px] flex items-center justify-center font-bold flex-shrink-0">
                    {action.badge}
                  </span>
                )}
              </Link>
            ))}
          </div>
        </div>

        {/* ── Platform-wide Session History ── */}
        <div>
          <div className="flex items-center gap-2 mb-5">
            <History size={20} className="text-forest-600" />
            <h3 className="font-serif text-xl font-semibold text-forest-900">Platform Session History</h3>
            <span className="text-xs text-forest-400">— all sessions across all users</span>
          </div>
          <ActivityHistory
            bookings={allBookings}
            canvasItems={[]}
            role="ADMIN"
          />
        </div>

      </div>
    </DashboardLayout>
  );
};

export default AdminDashboardPage;